/**
 * OpenClawBrain — Edit OpenClaw brain files remotely via runner terminal
 * Vertical menu with file categories + editor + tutorial hints
 */
import { useState, useEffect, useRef } from 'preact/hooks';
import { api } from '../api/client';
import { isDockerMode } from '../lib/openclawPaths';
import { useOpenClawAgentInfo } from '../hooks/useOpenClawAgentInfo';
import { LockOverlay } from './LockOverlay';
import { SkeletonBar } from './ui';
import { T } from '../styles/tokens';
import { Button, Textarea } from './ui';

interface Props {
  runnerId: string;
  isOnline: boolean;
  openclawPath?: string;
  openclawBase?: string;
  cloudMachineId?: string;
  locked?: boolean;
  editorData?: any;
}

interface BrainFile {
  id: string;
  label: string;
  icon: string;
  path: string; // relative to ~/.openclaw/workspace/
  category: string;
  description: string;
  tutorial: string; // markdown-like guidance
  template: string; // default content if file doesn't exist
}

const BRAIN_FILES: BrainFile[] = [
  {
    id: 'soul',
    label: 'Personalidade',
    icon: 'P',
    path: 'SOUL.md',
    category: 'IDENTIDADE',
    description: 'Quem o agente É — nome, tom, valores, limites',
    tutorial: `# Como configurar a Personalidade

**Este é o arquivo mais importante.** Define quem o agente é.

## O que colocar aqui:
- **Nome** — Como o agente se apresenta
- **Tom** — Formal? Casual? Técnico? Divertido?
- **Papel** — Ex: "Assistente pessoal", "Engenheiro sênior"
- **Limites** — O que ele NUNCA deve fazer
- **Valores** — O que ele prioriza

## Exemplo simples:
\`\`\`markdown
# Quem sou
Sou o Alex, assistente pessoal do João.

## Como me comporto
- Casual e direto
- Sempre em português
- Emojis com moderação
- Proativo em sugestões

## O que faço
- Pesquisas e análises
- Programação e automação
- Organização e lembretes

## Limites
- Nunca apagar dados sem confirmar
- Sempre pedir permissão para ações externas
\`\`\`

Dica: Quanto mais específico, melhor o agente se comporta.`,
    template: `# Quem sou

[Nome do agente] — assistente pessoal.

## Como me comporto
- Casual e direto
- Sempre em português
- Emojis com moderação 
- Proativo em sugestões

## O que faço
- Pesquisas e análises
- Programação e automação
- Organização e lembretes

## Limites
- Nunca apagar dados sem confirmar
- Sempre pedir permissão para ações externas
- Tudo é sigiloso
`,
  },
  {
    id: 'user',
    label: 'Perfil do Usuário',
    icon: 'U',
    path: 'USER.md',
    category: 'IDENTIDADE',
    description: 'Quem é o dono — nome, preferências, horários',
    tutorial: `# Como configurar o Perfil do Usuário

**O agente precisa saber quem você é** para responder melhor.

## O que colocar aqui:
- **Nome** — Como quer ser chamado
- **Timezone** — Para horários corretos
- **Idioma** — Português, inglês, etc.
- **Horário ativo** — Quando está disponível
- **Preferências** — Como gosta das respostas

## Exemplo:
\`\`\`markdown
# USER.md
- Nome: João
- Timezone: America/Sao_Paulo
- Idioma: Português
- Horário ativo: 09h às 18h

## Preferências
- Respostas diretas e objetivas
- Nível técnico: avançado
- Emojis: ok com moderação
\`\`\`

Dica: Inclua seus interesses e área profissional para respostas mais relevantes.`,
    template: `# USER.md

- **Nome:** [Seu nome]
- **Timezone:** America/Sao_Paulo
- **Idioma:** Português
- **Horário ativo:** 09h às 18h

## Perfil Profissional
- [Sua área de atuação]

## Preferências
- Comunicação direta e objetiva
- Emojis: ok com moderação
`,
  },
  {
    id: 'agents',
    label: 'Regras do Agente',
    icon: 'R',
    path: 'AGENTS.md',
    category: 'COGNITIVO',
    description: 'Como o agente pensa e age — regras, fluxos, integrações',
    tutorial: `# Como configurar as Regras do Agente

**Define o comportamento operacional** — como ele trabalha no dia a dia.

## O que colocar aqui:
- **Conectividade** — Telegram, Discord, etc.
- **Fluxo de trabalho** — O que fazer antes de cada tarefa
- **Memória** — Como organizar anotações
- **Segurança** — Regras absolutas
- **Ferramentas** — Quais integrações usar

## Seções recomendadas:
1. **Identidade & Conectividade** — IDs, canais
2. **Memória e Anotações** — Onde salvar o quê
3. **Regras de Ouro** — Nunca quebrar
4. **Segurança** — Limites absolutos
5. **Ações Externas** — Quando pedir permissão

Dica: Comece simples e vá adicionando regras conforme necessário.`,
    template: `# AGENTS.md

## Identidade & Conectividade
- **Canal:** Telegram

## Memória e Anotações
- Manter notas organizadas
- Resumir conversas importantes

## Regras de Ouro
1. Perguntar antes de agir em ações externas
2. Sempre consultar contexto antes de responder
3. Ser proativo em sugestões

## Segurança
- Nunca apagar arquivos sem confirmação
- Nunca enviar dados pessoais para terceiros
- Tudo é sigiloso
`,
  },
  {
    id: 'memory',
    label: 'Memória',
    icon: 'M',
    path: 'MEMORY.md',
    category: 'MEMÓRIA',
    description: 'Índice de memória de longo prazo — decisões, contexto, histórico',
    tutorial: `# Como configurar a Memória

**A memória é o que faz o agente parecer inteligente.** Ele lembra de tudo.

## Estrutura:
- **MEMORY.md** — Índice geral (este arquivo)
- **memory/YYYY-MM-DD.md** — Diário de cada dia
- **memory/topics/*.md** — Temas específicos

## O que colocar aqui:
- Decisões importantes com data
- Contexto de projetos ativos
- Regras aprendidas
- Status de integrações

## Como funciona:
1. O agente lê MEMORY.md antes de cada tarefa
2. Busca detalhes nos topics quando necessário
3. Anota no diário o que fez cada dia

Dica: Comece vazio — o agente vai preenchendo automaticamente conforme vocês trabalham juntos.`,
    template: `# MEMORY.md — Índice de Memória

> O agente lê este arquivo antes de cada tarefa.

## Decisões Importantes
- [Adicione decisões aqui conforme surgirem]

## Projetos Ativos
- [Liste seus projetos]

## Regras Aprendidas
- [O agente adiciona regras que aprende]
`,
  },
  {
    id: 'tools',
    label: 'Ferramentas',
    icon: 'T',
    path: 'TOOLS.md',
    category: 'AÇÕES',
    description: 'Ferramentas e integrações disponíveis — APIs, credenciais',
    tutorial: `# Como configurar as Ferramentas

**Define o que o agente consegue FAZER** além de conversar.

## Integrações comuns:
- **Email** — Gmail, Outlook
- **Trello/Notion** — Gerenciar tarefas
- **GitHub** — Código e deploy
- **Cloudflare** — DNS, hosting
- **SEO Tools** — SEMrush, Ahrefs

## Como adicionar uma ferramenta:
\`\`\`markdown
## Trello
- **API Key:** Em \`.env\` (TRELLO_API_KEY)
- **Token:** Em \`.env\` (TRELLO_TOKEN)
- **Status:** Operacional
\`\`\`

## Segurança:
- NUNCA coloque senhas/tokens neste arquivo
- Use variáveis de ambiente (.env)
- Este arquivo é apenas documentação

Dica: Comece com 1-2 ferramentas e vá expandindo.`,
    template: `# TOOLS.md — Ferramentas & Integrações

## Segurança
- Credenciais ficam em \`.env\` (nunca neste arquivo!)
- Template: \`.env.example\`

## Ferramentas Configuradas
- [Adicione suas integrações aqui]

## Pendências
- [ ] Configurar primeira integração
`,
  },
  {
    id: 'heartbeat',
    label: 'Tarefas Automáticas',
    icon: 'A',
    path: 'HEARTBEAT.md',
    category: 'AUTOMAÇÃO',
    description: 'Tarefas periódicas — lembretes, relatórios, monitoramento',
    tutorial: `# Como configurar Tarefas Automáticas

**O agente pode executar tarefas sozinho** em horários definidos.

## Exemplos:
- Relatório diário às 23h
- Monitorar sites a cada 6h
- Backup de memória diário
- Verificar emails de manhã

## Formato:
\`\`\`markdown
## Tarefa: Relatório Diário
- **Quando:** Todo dia às 23:00
- **O quê:** Resumir atividades do dia
- **Como:** Ler memory/YYYY-MM-DD.md e consolidar
\`\`\`

## Importante:
- O agente verifica este arquivo periodicamente
- Tarefas devem ter horário e ação claros
- Use timezone correto (America/Sao_Paulo)

Dica: Comece sem tarefas automáticas e adicione conforme sentir necessidade.`,
    template: `# HEARTBEAT.md — Tarefas Automáticas

> O agente verifica este arquivo periodicamente.

## Tarefas Ativas
- Nenhuma configurada ainda

## Como Adicionar
1. Descreva a tarefa com horário e ação
2. O agente vai executar automaticamente
`,
  },
  {
    id: 'config',
    label: 'Configuração Geral',
    icon: 'C',
    path: 'config.yaml',
    category: 'SISTEMA',
    description: 'Config geral — modelo, canais, limites',
    tutorial: `# Configuracao Geral

**Este é o arquivo de sistema.** Edite com cuidado.

## Campos principais:
- **name** — Nome do agente
- **model** — Modelo de IA (ex: anthropic/claude-sonnet-4-5)
- **channels** — Canais de comunicação (Telegram, etc.)

## Exemplo:
\`\`\`yaml
name: "Meu Agente"
model: "anthropic/claude-sonnet-4-5"
channels:
  - type: telegram
    token: "SEU_TOKEN_AQUI"
\`\`\`

 **Cuidado:** Alterações erradas podem quebrar o agente. Use a tela de Configuração para mudanças simples.

Dica: Prefira usar a tela Geral para mudancas no modelo e API key.`,
    template: `name: "Meu Agente"
model: "anthropic/claude-sonnet-4-5"
`,
  },
];

export function OpenClawBrain({ runnerId, isOnline, openclawPath, openclawBase, cloudMachineId, locked, editorData }: Props) {
  const agentInfo = useOpenClawAgentInfo(runnerId, isOnline, openclawPath);
  const [selectedFile, setSelectedFile] = useState<BrainFile>(BRAIN_FILES[0]);
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [fileExists, setFileExists] = useState(true);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  
  // Agent config expansion
  const [showAgentConfig, setShowAgentConfig] = useState(false);
  const [agentName, setAgentName] = useState(agentInfo.name || '');
  const [agentModel, setAgentModel] = useState(agentInfo.model || '');
  const [savingAgentConfig, setSavingAgentConfig] = useState(false);
  // Cache for all preloaded files
  const fileCacheRef = useRef<Record<string, { content: string; exists: boolean }>>({});
  const preloadedRef = useRef(false);

  // Queue lock: check if there are pending brain_save actions (non-blocking)
  const [queueLockMsg, setQueueLockMsg] = useState('');

  useEffect(() => {
    if (!cloudMachineId) return;
    let cancelled = false;
    async function check() {
      try {
        const res = await api.get(`/cloud/machines/${cloudMachineId}/brain-queue-status`) as any;
        if (cancelled) return;
        const pending = (res?.pending) || 0;
        const running = (res?.running) || 0;
        if (pending > 0 || running > 0) {
 setQueueLockMsg(` ${pending + running} arquivo(s) na fila de salvamento.`);
        } else {
          setQueueLockMsg('');
        }
      } catch {
        // silently ignore — don't block UI
      }
    }
    check();
    const iv = setInterval(check, 8000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [cloudMachineId]);

  // runCommand removed — all operations via D1/API now

  // Detect OpenClaw base path
  const ocBaseRef = useRef<string | null>(null);
  function getOcBase(): string {
    if (openclawBase) return openclawBase;
    if (ocBaseRef.current) return ocBaseRef.current;
    if (isDockerMode(openclawPath)) return '/data/.openclaw';
    // data: prefix means explicit data dir
    if (openclawPath?.startsWith('data:')) return openclawPath.replace('data:', '');
    // Binary paths (/usr/bin/openclaw, /usr/local/bin/openclaw) → detect base
    // Default to /root/.openclaw for Linux servers, ~/.openclaw otherwise
    return '/root/.openclaw';
  }

  function getFilePath(file: BrainFile): string {
    const base = getOcBase();
    return file.path === 'config.yaml'
      ? `${base}/agents/main/agent/config.yaml`
      : `${base}/workspace/${file.path}`;
  }

  function cleanOutput(output: string): string {
    return output.split('\n').filter(line => {
      const l = line.trim();
      return l && !l.startsWith('dircolors:') && !l.startsWith('bash:') && !l.startsWith('mesg:') && !l.startsWith('warning:');
    }).join('\n').trim();
  }

  // PRELOAD ALL FILES — editorData first (D1 instant), then API, then runner command
  async function preloadAllFiles() {
    setLoading(true);
    try {
      // Strategy 0: Use editorData from D1 (instant, no SSH)
      if (editorData?.synced && (editorData.brain_files || editorData.workspace_files)) {
        console.log('[Brain] Loading from D1 editorData (instant)');
        const bf = editorData.brain_files || {};
        const wf = editorData.workspace_files || {};
        const fileMap: Record<string, string | null> = {
          soul: bf.soul_md, user: bf.user_md, memory: bf.memory_md, tools: bf.tools_md,
          agents: wf.agents_md, heartbeat: wf.heartbeat_md, identity: wf.identity_md, bootstrap: wf.bootstrap_md,
        };
        const cache: Record<string, { content: string; exists: boolean }> = {};
        for (const file of BRAIN_FILES) {
          const val = fileMap[file.id] ?? null;
          cache[file.id] = { content: val || '', exists: val !== null && val !== '' };
        }
        fileCacheRef.current = cache;
        preloadedRef.current = true;
        const cur = cache[selectedFile.id];
        if (cur) {
          setContent(cur.content);
          setOriginalContent(cur.content);
          setFileExists(cur.exists);
        }
        setLoading(false);
        return;
      }

      // Strategy 1: API endpoint (reliable, same pattern as OpenClawConfig)
      if (cloudMachineId) {
        try {
          console.log('[Brain] Loading files via API for machine:', cloudMachineId);
          const res = await api.get(`/cloud/machines/${cloudMachineId}/brain-files`) as any;
          console.log('[Brain] API response:', JSON.stringify(res).substring(0, 200));
          const apiFiles = res?.data?.files || res?.files || {};

          const cache: Record<string, { content: string; exists: boolean }> = {};
          for (const file of BRAIN_FILES) {
            const apiFile = apiFiles[file.path];
            if (apiFile) {
              cache[file.id] = { content: apiFile.content || '', exists: apiFile.exists };
            } else {
              cache[file.id] = { content: '', exists: false };
            }
          }

          fileCacheRef.current = cache;
          preloadedRef.current = true;

          const cur = cache[selectedFile.id];
          if (cur) {
            setContent(cur.content);
            setOriginalContent(cur.content);
            setFileExists(cur.exists);
          }
          return; // API success — done
        } catch (apiErr) {
          console.warn('[Brain] API load failed, falling back to runner command:', apiErr?.message || apiErr);
        }
      }

      // No fallback — all loading via D1/API only
      console.warn('[Brain] No data available — editorData not synced and API failed');
    } catch (err) {
      console.error('Failed to preload brain files:', err);
    } finally {
      setLoading(false);
    }
  }

  // Load single file (fallback, or for refresh after save)
  async function loadFile(file: BrainFile) {
    // If preloaded, use cache (instant)
    const cached = fileCacheRef.current[file.id];
    if (cached !== undefined) {
      setContent(cached.content);
      setOriginalContent(cached.content);
      setFileExists(cached.exists);
      return;
    }

    // Reload ALL files via API (ensures correct paths)
    setLoading(true);
    setSaveResult(null);
    try {
      await preloadAllFiles();
    } catch (err) {
      console.error('Failed to load file:', err);
    } finally {
      setLoading(false);
    }
  }

  // Save file content — API-first (same pattern as OpenClawConfig), fallback to runner
  async function saveFile() {
    setSaving(true);
    setSaveResult(null);
    try {
      // Strategy 1: Queue-first (reliable), with direct API as optimization
      if (cloudMachineId) {
        // Enqueue brain_save via queue (runner handles path detection + write)
        const queueResult = await api.post(`/cloud/machines/${cloudMachineId}/queue-action`, {
          action: 'brain_save',
          payload: { file: selectedFile.path, content },
          source: 'brain_editor',
        }) as any;
        const queueData = queueResult?.data || queueResult;

        setOriginalContent(content);
        setFileExists(true);
        fileCacheRef.current[selectedFile.id] = { content, exists: true };

        // Also try direct save as optimization (faster feedback)
        try {
          await api.patch(`/cloud/machines/${cloudMachineId}/update-agent-brain`, {
            file: selectedFile.path,
            content,
          });
        } catch (directErr: any) {
          console.warn('[Brain] Direct save failed (queue already enqueued):', directErr?.message);
        }

        const immediate = queueData?.immediate || queueData?.status === 'done';
        const msg = selectedFile.path === 'config.yaml'
 ? (immediate ? 'Arquivo salvo! Aplicado.' : 'Arquivo salvo! Aplicando...')
 : (immediate ? 'Arquivo salvo com sucesso!' : 'Salvar enfileirado — será aplicado em breve');
        setSaveResult({ ok: true, msg });
        setTimeout(() => setSaveResult(null), 3000);
        return;
      }

      // No runner fallback — queue-based save only
      console.warn('[Brain] No cloudMachineId — cannot save without cloud machine');
      setSaveResult({ ok: false, msg: 'Erro: Maquina cloud nao configurada' });
      setTimeout(() => setSaveResult(null), 5000);
    } catch (err: any) {
 setSaveResult({ ok: false, msg: `Erro: ${err.message || 'Falha ao salvar'}` });
      setTimeout(() => setSaveResult(null), 5000);
    } finally {
      setSaving(false);
    }
  }

  // Save agent config (name + model) via queue
  async function saveAgentConfig() {
    setSavingAgentConfig(true);
    try {
      if (!cloudMachineId) throw new Error('Cloud machine ID not available');

      const changes: Record<string, any> = {};
      if (agentName.trim()) changes.name = agentName.trim();
      if (agentModel.trim()) changes.model = agentModel.trim();

      await api.post(`/cloud/machines/${cloudMachineId}/editor-save`, {
        tab: 'general',
        changes,
      });

      agentInfo.refetch();
      setSaveResult({ ok: true, msg: 'Configuracao do agente salva! Aplicando...' });
      setTimeout(() => setShowAgentConfig(false), 1500);
    } catch (err: any) {
      setSaveResult({ ok: false, msg: `Erro ao salvar: ${err.message || 'Falha na configuracao'}` });
    } finally {
      setSavingAgentConfig(false);
    }
  }

  // Sync agent info fields to local state
  useEffect(() => {
    setAgentName(agentInfo.name || '');
    setAgentModel(agentInfo.model || '');
  }, [agentInfo.name, agentInfo.model]);

  // Preload ALL brain files on mount (1 command = all files)
  // Padrão OpenClawConfig: deps incluem cloudMachineId
  useEffect(() => {
    if (isOnline && openclawPath) {
      preloadAllFiles();
    }
  }, [runnerId, isOnline, openclawPath, cloudMachineId]);

  // When file selection changes, use cache (instant) or fallback to individual load
  useEffect(() => {
    if (preloadedRef.current) {
      const cached = fileCacheRef.current[selectedFile.id];
      if (cached !== undefined) {
        setContent(cached.content);
        setOriginalContent(cached.content);
        setFileExists(cached.exists);
        setLoading(false);
        setSaveResult(null);
        return;
      }
    }
    if (isOnline) loadFile(selectedFile);
  }, [selectedFile.id]);

  const hasChanges = content !== originalContent;
  const categories = [...new Set(BRAIN_FILES.map(f => f.category))];

  if (!isOnline) {
    return (
      <div style={{
        padding: 24, marginBottom: 20, borderRadius: 14,
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        textAlign: 'center', color: 'var(--text-muted)',
      }}>
 <span style={{ fontSize: 32 }}></span>
        <p style={{ margin: '8px 0 0' }}>Máquina offline. Conecte para editar.</p>
      </div>
    );
  }

  // File tab colors for visual identity
  const tabColors: Record<string, string> = {
    soul: T.purple, user: T.primary, agents: '#F472B6', memory: T.success,
    tools: T.warning, heartbeat: '#FB923C', config: T.textMuted,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', margin: '-20px -24px', height: 'calc(85vh - 120px)', position: 'relative' }}>
      <LockOverlay locked={locked} />
      {/* ═══ FILE TABS — horizontal, scrollable ═══ */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: `1px solid ${T.whiteAlpha8}`,
        background: T.bg, overflowX: 'auto', flexShrink: 0,
      }}>
        {BRAIN_FILES.map(file => {
          const isActive = selectedFile.id === file.id;
          const cached = fileCacheRef.current[file.id];
          const exists = cached?.exists !== false;
          const color = tabColors[file.id] || T.textMuted;
          return (
            <Button variant="ghost"
              key={file.id}
              onClick={() => { setSelectedFile(file); }}
              style={{
                padding: '8px 14px', border: 'none', cursor: 'pointer',
                background: isActive ? T.bgCard : 'transparent',
                borderBottom: isActive ? `2px solid ${color}` : '2px solid transparent',
                borderRight: '1px solid rgba(255,255,255,0.04)',
                color: isActive ? T.text : T.textMuted,
                fontSize: 11, fontWeight: isActive ? 700 : 500,
                display: 'flex', alignItems: 'center', gap: 6,
                whiteSpace: 'nowrap' as const, transition: 'all 0.15s',
                opacity: exists ? 1 : 0.5,
              }}
            >
              <span style={{
                width: 6, height: 6, borderRadius: 3,
                background: isActive ? color : 'transparent',
                flexShrink: 0,
              }} />
              {file.path}
              {!exists && <span style={{ fontSize: 9, color: T.textDisabled }}>new</span>}
            </Button>
          );
        })}
      </div>

      {/* ═══ TOOLBAR ═══ */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '4px 12px', background: T.bg, borderBottom: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 8, height: 8, borderRadius: 4,
            background: tabColors[selectedFile.id] || T.textMuted,
          }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{selectedFile.label}</span>
          <span style={{ fontSize: 11, color: T.textDisabled }}>—</span>
          <span style={{ fontSize: 11, color: T.textMuted }}>{selectedFile.description}</span>
          {hasChanges && <span style={{ fontSize: 9, fontWeight: 700, color: T.warning, background: T.warningBg, padding: '1px 6px', borderRadius: 4 }}>MODIFICADO</span>}
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {!fileExists && !loading && (
            <Button variant="ghost" onClick={() => setContent(selectedFile.template)} style={{
              padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
              background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.2)',
              color: T.success, cursor: 'pointer',
            }}>+ Template</Button>
          )}
          <Button variant="ghost" onClick={() => setShowTutorial(!showTutorial)} style={{
            padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
            background: showTutorial ? T.accentBg : T.bgCard,
            border: '1px solid ' + (showTutorial ? T.accentBg : T.border),
            color: showTutorial ? T.accent : T.textMuted, cursor: 'pointer',
          }}>?</Button>
          <Button variant="ghost" onClick={() => { agentInfo.refetch(); preloadAllFiles(); }} style={{
            padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
            background: T.bgCard, border: '1px solid rgba(255,255,255,0.06)',
            color: T.textMuted, cursor: 'pointer',
          }}>↻</Button>
        </div>
      </div>

      {/* Queue lock banner */}
      {queueLockMsg && (
        <div style={{
          padding: '4px 12px', fontSize: 11, fontWeight: 600, flexShrink: 0,
          background: `${T.warningBgSubtle}`, color: T.warning,
          borderBottom: `1px solid ${T.warningBg}`,
        }}>
          {queueLockMsg}
        </div>
      )}

      {/* Save result bar */}
      {saveResult && (
        <div style={{
          padding: '3px 12px', fontSize: 11, fontWeight: 600, flexShrink: 0,
          background: saveResult.ok ? 'rgba(52,211,153,0.08)' : T.dangerBg,
          color: saveResult.ok ? T.success : T.danger,
        }}>{saveResult.msg}</div>
      )}

      {/* Tutorial panel */}
      {showTutorial && (
        <div style={{
          padding: '8px 12px', background: T.accentBg,
          borderBottom: `1px solid ${T.accentBg}`,
          fontSize: 11, lineHeight: 1.6, color: T.textSecondary,
          maxHeight: 150, overflowY: 'auto', flexShrink: 0,
        }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{selectedFile.tutorial}</pre>
        </div>
      )}

      {/* ═══ EDITOR ═══ */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: T.textMuted }}>
            <div style={{ width: 18, height: 18, border: '2px solid #1E293B', borderTopColor: T.textMuted, borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 8 }} />
            <div style={{ padding: 8 }}><SkeletonBar width="50%" height={14} /></div>
          </div>
        ) : !fileExists && !content ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, color: T.textMuted }}>
 <div style={{ fontSize: 32, opacity: 0.3 }}></div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Arquivo não existe</div>
            <div style={{ fontSize: 12, color: T.textDisabled }}>Crie a partir do template ou escreva do zero</div>
            <Button variant="ghost" onClick={() => { setContent(selectedFile.template); }} style={{
              marginTop: 4, padding: '8px 20px', borderRadius: 6, background: T.bgHover, border: '1px solid rgba(255,255,255,0.1)', color: T.text, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>Criar com Template</Button>
          </div>
        ) : (
          <Textarea
            ref={editorRef}
            value={content}
            onInput={(e: any) => setContent(e.target.value)}
            spellCheck={false}
            style={{
              width: '100%', height: '100%',
              padding: '10px 14px',
              background: T.bg, color: T.textSecondary,
              border: 'none', outline: 'none', resize: 'none',
              fontSize: 13, fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'SF Mono', monospace",
              lineHeight: 1.7, boxSizing: 'border-box', tabSize: 2,
              caretColor: tabColors[selectedFile.id] || T.purple,
            }}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Tab') {
                e.preventDefault();
                const target = e.target as HTMLTextAreaElement;
                const start = target.selectionStart;
                const end = target.selectionEnd;
                setContent(content.substring(0, start) + '  ' + content.substring(end));
                setTimeout(() => { target.selectionStart = target.selectionEnd = start + 2; }, 0);
              }
              if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                if (hasChanges) saveFile();
              }
            }}
          />
        )}
      </div>

      {/* ═══ STATUS BAR ═══ */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '4px 12px', background: T.bgCard, borderTop: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', gap: 12, fontSize: 10, color: T.textDisabled }}>
          <span style={{ color: tabColors[selectedFile.id] || T.textMuted, fontWeight: 600 }} title={getFilePath(selectedFile)}>{getFilePath(selectedFile)}</span>
          <span>Ln {content.split('\n').length}</span>
          <span>{new Blob([content]).size}B</span>
          <span>UTF-8</span>
          <span>Markdown</span>
          {agentInfo.status === 'synced' && agentInfo.lastSynced && (
            <span style={{ color: T.success }}>{agentInfo.lastSynced.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
          )}
        </div>
        <Button variant="ghost"
          onClick={saveFile}
          disabled={saving || !hasChanges}
          style={{
            padding: '3px 14px', borderRadius: 4, fontSize: 11, fontWeight: 700, border: 'none',
            background: hasChanges
              ? `linear-gradient(135deg, ${tabColors[selectedFile.id] || T.accent}, ${tabColors[selectedFile.id] || T.accent}88)`
              : T.bgCard,
            color: hasChanges ? '#fff' : T.textDisabled,
            cursor: hasChanges && !saving ? 'pointer' : 'default',
            opacity: saving ? 0.6 : 1, transition: 'all 0.2s',
            boxShadow: hasChanges ? `0 2px 8px ${tabColors[selectedFile.id] || T.accent}40` : 'none',
          }}
        >
          {saving ? '...' : hasChanges ? '⌘S Salvar' : 'Salvo'}
        </Button>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
